import { Box, Typography } from "@mui/material";
import Flex from "../../../../../components/Flex";
import OutlineCard from "../../../../../components/OutlineCard";
import CustomTitle from "../../../../../components/app/CustomTitle";
import { getXtenantId } from "../../../../../utils/utils";
import { useNavigate } from "react-router-dom";
import { PROVIDER_PATHS } from "../../../../../constants/paths";
import { useDashboardStatusCount } from "../../../../../queries/dashboard/use-dashboard-status-count";

interface ActiveCaseCard {
  key: string;
  label: string;
  subtext: string;
  tooltip: string;
  bg: string;
  border: string;
  navigateTo: string;
}

const ACTIVE_CASE_CARDS: ActiveCaseCard[] = [
  {
    key: "newAssessment",
    label: "New Assessment",
    subtext: "Review Pending",
    tooltip:
      "Assessments completed by patient with provider review status pending",
    bg: "#F5F5F5",
    border: "#E0E0E0",
    navigateTo: `${PROVIDER_PATHS.ASSESSMENTS}?reviewStatus=Pending`,
  },
  {
    key: "highRisk",
    label: "High Risk",
    subtext: "Severity Critical",
    tooltip:
      "Open assessments with critical severity score, not yet acknowledged",
    bg: "#F5F5F5",
    border: "#E0E0E0",
    navigateTo: `${PROVIDER_PATHS.ASSESSMENTS}?severity=HIGH&status=Open`,
  },
  {
    key: "upcomingAppointment",
    label: "Upcoming Appointment",
    subtext: "Open Appointments",
    tooltip:
      "Scheduled or confirmed appointments from today onwards, excluding cancelled/completed",
    bg: "#F5F5F5",
    border: "#E0E0E0",
    navigateTo: `${PROVIDER_PATHS.APPOINTMENTS}?status=PENDING,CONFIRMED`,
  },
  {
    key: "openEncounter",
    label: "Open Visit",
    subtext: "Visit In Progress",
    tooltip: "Visits with status in progress or unsigned notes",
    bg: "#F5F5F5",
    border: "#E0E0E0",
    navigateTo: `${PROVIDER_PATHS.OPEN_ENCOUNTERS}?status=InProgress,Unsigned`,
  },
  {
    key: "openTasks",
    label: "Open Tasks",
    subtext: "Open & Due Tasks",
    tooltip: "Tasks that are not completed with due date on or before today",
    bg: "#F5F5F5",
    border: "#E0E0E0",
    navigateTo: `/app/provider/collaboration/tasks?activeTask=provider-centric-tasks&status=Open&myTasks=false`,
  },
  {
    key: "fax",
    label: "Fax",
    subtext: "Not Reviewed",
    tooltip:
      "Fax items with review status pending, assigned to current user or unassigned",
    bg: "#F5F5F5",
    border: "#E0E0E0",
    navigateTo: PROVIDER_PATHS.INCOMING_FAX,
  },
];

const DashBoardAppointmentStatusSection = () => {
  const { data } = useDashboardStatusCount({ xTENANTID: getXtenantId() });
  const navigate = useNavigate();

  const formatCount = (count: number) =>
    count < 10 ? `0${count}` : `${count}`;

  return (
    <OutlineCard sx={{ borderRadius: "8px" }}>
      <CustomTitle sx={{ mb: 1.5 }}>Active Cases</CustomTitle>

      <Flex gap="12px" overflow="auto" sx={{ scrollbarWidth: "none" }}>
        {ACTIVE_CASE_CARDS.map((card) => (
          <Box
            key={card.key}
            onClick={() => navigate(card.navigateTo)}
            sx={{
              background: card.bg,
              border: `1px solid ${card.border}`,
              cursor: "pointer",
              borderRadius: "8px",
              padding: "12px",
              minWidth: 120,
              height: 71,
              flex: "1 1 0",
              boxSizing: "border-box",
            }}
          >
            <Typography
              fontSize={14}
              fontWeight={400}
              color="#0E151D"
              noWrap
              lineHeight={1.2}
              sx={{ mb: 1 }}
            >
              {card.label}
            </Typography>
            <Typography
              fontSize={20}
              fontWeight={600}
              color="#373D41"
              lineHeight={1.2}
            >
              {formatCount(
                (data?.data as Record<string, number>)?.[card.key] || 0
              )}
            </Typography>
          </Box>
        ))}
      </Flex>
    </OutlineCard>
  );
};

export default DashBoardAppointmentStatusSection;
